import { motion } from 'framer-motion';
import styled from 'styled-components';

import { setTypography, setVh } from 'utils/styles/mixins';
import {
  mediaDesktop,
  mediaDesktopXLarge,
  mediaTablet,
} from 'utils/styles/responsive';
import { colors } from 'utils/styles/theme';

export const Container = styled(motion.div)<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100vw;
  min-height: ${setVh(100)};
  height: 100%;
  background: ${colors.pearlBush};
  transform: translateX(${({ isOpen }) => (isOpen ? '0%' : '125%')});
  z-index: 5;
  transition-property: all;
  transition-duration: 0.9s;
  overflow: hidden;

  ${mediaTablet`
    width: 75vw;
    overflow: visible;
  `}
`;

export const ContentWrapper = styled(motion.div)`
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  height: 100%;
  padding: 82rem 48rem;

  & > div:first-of-type {
    &:before {
      width: 70%;
    }
  }

  ${mediaTablet`
    padding: 60rem 90rem 120rem 300rem;
  `}

  ${mediaDesktop`
    padding: 130rem 90rem 120rem 670rem;

    & > div:first-of-type {
      &:before {
        width: 100%;
      }
    }
  `}
`;

export const ButtonWrapper = styled(motion.div)`
  position: absolute;
  top: 60rem;
  left: 60rem;
  z-index: 4;
  display: none;
  :focus-visible {
    outline: none;
  }

  ${mediaTablet`
    display: block;
  `}

  ${mediaDesktop`
    top: 108rem;
    left: 84rem;
  `}
`;

export const MobileButtonWrapper = styled(motion.button)`
  position: fixed;
  top: 36rem;
  right: 0;
  z-index: 5;
  display: block;

  svg {
    width: 220rem;
  }

  ${mediaTablet`
    display: none;
    svg {
      width: 185rem;
    }
  `}
`;

export const ImageWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 32rem 0;
  pointer-events: none;
  user-select: none;

  img {
    max-width: 460rem;
  }

  ${mediaTablet`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    transform: translateX(-50%);

    img {
      max-width: 500rem;
    }
  `}

  ${mediaDesktop`
    img {
      max-width: 1115rem;
    }
  `}

  ${mediaDesktopXLarge`
    img {
      height: 800rem;
      max-width: none;
    }
  `}
`;

export const ChapterLabelWrapper = styled(motion.div)``;

export const Title = styled(motion.div)`
  color: ${colors.spicyMix};
  ${({ theme: { locale } }) => setTypography('heading2', locale)};
  white-space: pre-wrap;
  user-select: none;
  margin: 80rem 0 90rem;
`;

export const Description = styled(motion.div)`
  color: ${colors.taupe};
  ${({ theme: { locale } }) => setTypography('body1', locale)};
  white-space: pre-wrap;
  user-select: none;
  margin: 0 0 120rem;
`;

export const Columns = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 82rem;
  margin-bottom: 60rem;

  ${mediaDesktop`
    grid-template-columns: repeat(2, 1fr);
    margin-bottom: 120rem;
  `}
`;

export const Column = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 48rem;

  ${mediaDesktop`
    gap: 80rem;
  `}
`;

export const ColumnTitle = styled(motion.div)`
  color: ${colors.spicyMix};
  ${({ theme: { locale } }) => setTypography('heading3', locale)};
  white-space: pre-wrap;
  user-select: none;
`;

export const ColumnDescription = styled(motion.div)`
  color: ${colors.taupe};
  ${({ theme: { locale } }) => setTypography('body2', locale)};
  white-space: pre-wrap;
  user-select: none;
  position: relative;
  margin: 48rem 0 0;

  &:before {
    content: '';
    position: absolute;
    top: -48rem;
    left: 0;
    right: 0;
    width: 100%;
    height: 2px;
    background: ${colors.taupe};
  }
`;

export const ColumnLinks = styled(motion.ul)`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 32rem;
`;

export const Link = styled(motion.li)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  user-select: none;
  cursor: pointer;

  a {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 32rem;
    ${({ theme: { locale } }) => setTypography('body2', locale)};
    font-weight: 600;
    color: ${colors.taupe};

    svg {
      width: 80rem;
      height: 80rem;
      min-width: 80rem;
    }
  }

  ${mediaDesktop`
    a {
      svg {
        width: 60rem;
        height: 60rem;
        min-width: 60rem;
      }
    }
  `}
`;

export const Footer = styled(motion.div)`
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 30rem;

  & > div:last-of-type {
    width: 100%;
  }

  & > div:first-of-type {
    &:after {
      content: '';
      width: 1px;
      height: 30rem;
      display: inline-block;
      background: ${colors.taupe};
      margin-left: 30rem;
    }
  }

  ${mediaTablet`
    flex-wrap: wrap;

    & > div:last-of-type {
      width: auto;
    }
  `}
`;

export const FooterItem = styled(motion.div)`
  a,
  p {
    color: ${colors.taupe};
    ${({ theme: { locale } }) => setTypography('body4', locale)};
    user-select: none;
    display: inline-block;
  }
`;

export const Separator = styled(motion.div)`
  width: 1px;
  height: 30rem;
  display: inline-block;
  background: ${colors.taupe};
`;
